import type { MarketplaceKey } from "@/marketplaces/registry";

/** Compact policy bundle for LLM / image prompts (aligned with docs/marketplaces.md). */
export type MarketplacePolicy = {
  id: string;
  displayName: string;
  generatorMode: "single_pass_card" | "single_pass_safe" | "authentic_listing";
  compliance: Record<string, unknown>;
  visualProfile: Record<string, unknown>;
  defaultShots: readonly string[];
  factualityRules: Record<string, unknown>;
  /** Short English hint for image model tone. */
  imageToneHint: string;
};

export const MARKETPLACE_POLICIES: Record<MarketplaceKey, MarketplacePolicy> = {
  wb: {
    id: "wildberries",
    displayName: "Wildberries",
    generatorMode: "single_pass_card",
    compliance: {
      formats: ["jpg", "png", "webp"],
      minWidth: 700,
      minHeight: 900,
      maxImages: 30,
      preferredAspectRatio: "3:4",
      mainImageMustShowProductFully: true,
      preferNeutralBackground: true,
      bannedElements: [
        "prices",
        "discount labels",
        "qr codes",
        "contacts",
        "external links",
        "irrelevant objects",
        "text covering key product areas",
      ],
    },
    visualProfile: {
      infographicIntensity: "high",
      heroTextAllowed: true,
      textStyle: "bold_short_benefit_led",
      maxTextBlocksHero: 3,
      maxBadgesHero: 2,
      maxTextAreaRatio: 0.28,
      productDominanceRatio: 0.62,
      compositionBias: "thumbnail_ctr",
    },
    defaultShots: ["hero_benefit", "benefits_grid", "usage_scene", "details_closeup", "dimensions_or_comparison"],
    factualityRules: {
      allowOnlyVerifiedClaims: true,
      banFakePromoClaims: true,
      banFakeDiscounts: true,
    },
    imageToneHint:
      "Wildberries-style: competitive thumbnail, strong but clean infographic, product large and readable in grid; no acid colors or promo scams.",
  },
  ozon: {
    id: "ozon",
    displayName: "Ozon",
    generatorMode: "single_pass_card",
    compliance: {
      formats: ["jpg", "jpeg", "png", "webp", "heic"],
      recommendedWidth: 900,
      recommendedHeight: 1200,
      maxImages: 30,
      preferredAspectRatio: "3:4",
      firstFiveImagesCritical: true,
      mainImageMustShowProductFully: true,
    },
    visualProfile: {
      infographicIntensity: "medium",
      heroTextAllowed: true,
      textStyle: "clean_fact_led",
      maxTextBlocksHero: 2,
      maxBadgesHero: 1,
      maxTextAreaRatio: 0.22,
      productDominanceRatio: 0.68,
      compositionBias: "clarity_first",
    },
    defaultShots: ["hero_clean", "benefits_grid", "usage_scene", "details_closeup", "dimensions_or_kit"],
    factualityRules: {
      allowOnlyVerifiedClaims: true,
      banFakePromoClaims: true,
      banBrandMismatch: true,
    },
    imageToneHint:
      "Ozon-style: clean structured 3:4 card, factual overlays, trustworthy; hero at most one key message; typography legible.",
  },
  kaspi: {
    id: "kaspi",
    displayName: "Kaspi",
    generatorMode: "single_pass_safe",
    compliance: {
      formats: ["jpg", "png"],
      minWidth: 500,
      minHeight: 500,
      maxWidth: 5000,
      maxHeight: 5000,
      maxImages: 15,
      noWatermarks: true,
      productMustBeInFocus: true,
      noExtraItemsOutsideSet: true,
      setMustShowAllIncludedElements: true,
    },
    visualProfile: {
      infographicIntensity: "low",
      heroTextAllowed: false,
      textStyle: "minimal_factual",
      maxTextBlocksHero: 0,
      maxBadgesHero: 0,
      maxTextAreaRatio: 0.08,
      productDominanceRatio: 0.8,
      compositionBias: "accuracy_first",
    },
    defaultShots: ["hero_clean", "alternate_angle", "kit_contents", "details_closeup", "dimensions_or_label"],
    factualityRules: {
      allowOnlyVerifiedClaims: true,
      banFakePromoClaims: true,
      banHypeWords: true,
      banOriginalLikeClaims: true,
    },
    imageToneHint:
      "Kaspi-style: catalog-accurate, neutral studio, almost no on-image text; show real product, kit completeness, labels when relevant; no hype banners.",
  },
  flip: {
    id: "flip",
    displayName: "Flip.kz",
    generatorMode: "single_pass_safe",
    compliance: {
      preferredAspectRatio: "3:4",
      preferCleanCatalogStyle: true,
      preferProductFirstComposition: true,
    },
    visualProfile: {
      infographicIntensity: "low",
      heroTextAllowed: false,
      textStyle: "minimal_factual",
      maxTextBlocksHero: 0,
      maxBadgesHero: 0,
      maxTextAreaRatio: 0.1,
      productDominanceRatio: 0.76,
      compositionBias: "catalog_readability",
    },
    defaultShots: ["hero_clean", "alternate_angle", "details_closeup", "scale_or_context", "kit_or_backside"],
    factualityRules: {
      allowOnlyVerifiedClaims: true,
      banFakePromoClaims: true,
    },
    imageToneHint:
      "Flip-style: conservative ecommerce catalog, soft lighting, truthful packaging/materials; minimal or no text overlays.",
  },
  olx: {
    id: "olx",
    displayName: "OLX",
    generatorMode: "authentic_listing",
    compliance: {
      maxFileMb: 5,
      recommendedWidth: 2000,
      recommendedHeight: 2000,
      maxImages: 8,
      bannedElements: [
        "contact_info_in_image",
        "competitor_platform_logos",
        "competitor_platform_watermarks",
        "collages",
        "overdecorated_ad_banners",
      ],
    },
    visualProfile: {
      infographicIntensity: "none",
      heroTextAllowed: false,
      maxTextBlocksHero: 0,
      maxTextAreaRatio: 0,
      productDominanceRatio: 0.78,
      compositionBias: "trust_and_condition",
    },
    defaultShots: ["hero_real", "alternate_angle", "details_closeup", "condition_marks", "kit_or_serial"],
    factualityRules: {
      allowOnlyVerifiedClaims: true,
      banGeneratedPromoLook: true,
      preserveRealCondition: true,
    },
    imageToneHint:
      "OLX-style: enhance real listing photos only — better light/sharpness, neutralize messy background if needed; NO marketing banners, NO added text, preserve wear/defects; same product identity.",
  },
  generic: {
    id: "generic",
    displayName: "Универсально",
    generatorMode: "single_pass_safe",
    compliance: {
      formats: ["jpg", "png", "webp"],
      preferredAspectRatio: "3:4",
      preferNeutralBackground: true,
      maxImages: 30,
    },
    visualProfile: {
      infographicIntensity: "low",
      heroTextAllowed: true,
      textStyle: "clean_neutral",
      maxTextBlocksHero: 2,
      maxBadgesHero: 1,
      maxTextAreaRatio: 0.2,
      productDominanceRatio: 0.7,
      compositionBias: "versatile_commerce",
    },
    defaultShots: ["hero_clean", "benefits_grid", "usage_scene", "details_closeup", "dimensions_or_kit"],
    factualityRules: {
      allowOnlyVerifiedClaims: true,
      banFakePromoClaims: true,
      banFakeDiscounts: true,
    },
    imageToneHint:
      "Neutral ecommerce product content: clean lighting, product-forward, moderate optional infographic; no marketplace-specific promo rules; versatile for own store, social, or unspecified channel.",
  },
};

export function getMarketplacePolicy(marketplace: MarketplaceKey): MarketplacePolicy {
  return MARKETPLACE_POLICIES[marketplace] ?? MARKETPLACE_POLICIES.wb;
}

export function getMarketplacePolicyJson(marketplace: MarketplaceKey): string {
  return JSON.stringify(getMarketplacePolicy(marketplace), null, 2);
}
